import { useMemo, useState } from 'react'
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table'
import { Button, Flex, Group, Modal, Text } from '@mantine/core'
import { OffersTable } from '@/components/offers/offers-table/offers-table'
import { DEFAULT_PAGINATION_SIZE } from '@shared/components/table/table'
import { GetOfferStationsGql } from '@/graphql/queries/offers/get-offer-stations.graphql'
import { ListOffersGql } from '@/graphql/queries/offers/list-offers.graphql'
import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { ListOffersOrderByOptions } from '@loom-api-types'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { useDisclosure } from '@mantine/hooks'
import { IconCancel } from '@tabler/icons-react'
import { CancelOfferGql } from '@/graphql/mutations/offers/cancel-offer.graphql'
import { showSuccessNotification } from '@shared/helpers/notifications/notifications'
import { useQueryClient } from '@tanstack/react-query'

type OfferToCancel = {
  id: string
  creator: string
  sent: string
  stationName: string
} | null

export function OffersList({
  stationRecordNumber,
  seasonFilterValue,
}: {
  stationRecordNumber?: string
  seasonFilterValue: number | undefined
}) {
  const queryClient = useQueryClient()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGINATION_SIZE,
  })
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [offerToCancel, setOfferToCancel] = useState<OfferToCancel>(null)
  const [isCancelModalOpen, { open: openCancelModal, close: closeCancelModal }] =
    useDisclosure(false)

  const stationsToDisplay = stationRecordNumber
    ? [stationRecordNumber]
    : (columnFilters.find((filter) => filter.id === 'stationName')?.value as string)

  const queryKey = useMemo(() => {
    return ['listOffers', pagination, columnFilters, stationsToDisplay, seasonFilterValue]
  }, [pagination, columnFilters, stationsToDisplay, seasonFilterValue])

  // Create a unique key for the table to force re-render stations change
  const tableRenderKey = useMemo(() => {
    return JSON.stringify({ stationRecordNumber, stationsToDisplay })
  }, [stationRecordNumber, stationsToDisplay])

  const { data, isError, isFetching, isLoading } = useAuthenticatedGraphQuery({
    queryKey,
    gql: ListOffersGql,
    queryParams: {
      currentPage: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      orderBy: ListOffersOrderByOptions.Created,
      orderByAscending: false,
      stationRecordNumbers: stationsToDisplay,
      offerStatuses: columnFilters.find((filter) => filter.id === 'status')?.value as string,
      season: seasonFilterValue,
    },
  })
  const {
    data: offerStationsData,
    isError: isOfferStationsError,
    isFetching: isOfferStationsFetching,
    isLoading: isOfferStationsLoading,
  } = useAuthenticatedGraphQuery({
    queryKey: ['getOfferStations'],
    gql: GetOfferStationsGql,
  })

  // Mutation to cancel the offer
  const cancelOfferMutation = useAuthenticatedGraphMutation({
    gql: CancelOfferGql,
    gqlKey: 'cancelOffer',
    onSuccess: () => {
      showSuccessNotification('Offer cancelled')
    },
  })

  const askForCancelConfirmation = (offerId: string) => {
    const offer = data?.listOffers?.results?.find((offer) => offer.id === offerId)
    setOfferToCancel(
      offer
        ? {
            id: offerId,
            creator: offer.createdBy ?? '',
            sent: offer.created,
            stationName: offer.stationRecordNumber
              ? `${offer.stationBrand} ${offer.stationRecordNumber}`
              : '',
          }
        : null
    )
    openCancelModal()
  }

  const abandonCancelOffer = () => {
    setOfferToCancel(null)
    closeCancelModal()
  }

  const confirmCancelOffer = () => {
    if (offerToCancel?.id) {
      cancelOfferMutation.mutate(
        { offerId: offerToCancel.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey })
          },
        }
      )
    }
    setOfferToCancel(null)
    closeCancelModal()
  }

  return (
    <>
      <OffersTable
        data={data?.listOffers}
        renderKey={tableRenderKey}
        offerStationsData={offerStationsData?.getOfferStations}
        isError={isError || isOfferStationsError}
        isFetching={isFetching || isOfferStationsFetching}
        isLoading={isLoading || isOfferStationsLoading}
        pagination={pagination}
        setPagination={setPagination}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        cancelOffer={askForCancelConfirmation}
        singleStationMode={!!stationRecordNumber}
      />

      <Modal
        data-testid='cancel-offer-modal'
        title={
          <Flex justify='flex-start' align='center'>
            <IconCancel size={28} color='var(--mantine-color-gray-4)' />
            <Text fw={500} size='lg' ml='sm'>
              Cancel offer
            </Text>
          </Flex>
        }
        opened={isCancelModalOpen}
        onClose={closeCancelModal}
        centered
        closeButtonProps={{ 'aria-label': 'Close modal' }}
        returnFocus={false}
      >
        Are you sure you want to cancel this offer?
        {offerToCancel && (
          <Text size='sm' c='dimmed' mt='xs'>
            Created for {offerToCancel.stationName} by {offerToCancel.creator} on{' '}
            {formatDate(new Date(offerToCancel.sent), 'DD/MM/YYYY')}
          </Text>
        )}
        <Group justify='flex-end' mt='md' gap='xs'>
          <Button variant='transparent' onClick={abandonCancelOffer}>
            No
          </Button>
          <Button color='red' onClick={confirmCancelOffer}>
            Yes, cancel
          </Button>
        </Group>
      </Modal>
    </>
  )
}
